import { makeArticle } from "./learnFactory";

export const pageCantStopCrying = makeArticle({
  slug: "cant-stop-crying-for-no-reason",
  title: "Can't Stop Crying for No Reason? Causes & Help | Neuro Wellness",
  description:
    "Crying a lot without knowing why can signal depression, stress, hormone changes or a neurological condition. Learn the causes and what to do next.",
  canonical: "/learn/cant-stop-crying-for-no-reason",
  h1: "Can't Stop Crying for No Reason? What It Can Mean and What Helps",
  breadcrumbLabel: "Crying for No Reason",
  shortAnswer:
    "Frequent crying that seems to come from nowhere is often a sign that something underneath needs attention. Common causes include depression, built-up stress or burnout, anxiety, grief, poor sleep, and hormone changes such as premenstrual, postpartum and perimenopausal shifts. Rarely, sudden crying that doesn't match how you feel is caused by a neurological condition called pseudobulbar affect. If crying spells have lasted two weeks or more, or come with low mood or hopelessness, talk to a clinician. If you are thinking about harming yourself, call or text 988 now.",
  keyTakeaways: [
    "“For no reason” usually means “for a reason I haven't named yet.”",
    "Tearfulness is a common sign of depression, especially with low mood or loss of interest lasting two weeks or more.",
    "Hormone changes and some neurological conditions can also cause crying spells.",
    "If you are thinking about harming yourself, call or text 988 now.",
  ],
  sections: [
    {
      heading: "If you're in crisis, get help now",
      paragraphs: [
        "If you are having thoughts of ending your life or harming yourself, call or text 988 (Suicide and Crisis Lifeline) now, or go to your nearest emergency room. If you are in immediate danger, call 911.",
        "If you recently had a baby and are crying constantly, can't sleep even when the baby sleeps, or are having frightening thoughts, contact your doctor today. See our [[crisis resources|/crisis-resources]].",
      ],
    },
    {
      heading: "Common causes of crying spells",
      paragraphs: [
        "Crying is normal and healthy. But if you're tearing up in the car, at your desk or mid-conversation and can't point to why, it can feel frightening and embarrassing. You are not “too sensitive,” and you are not alone. Common causes include:",
      ],
      list: [
        "**Depression.** Tearfulness often comes with sadness or emptiness, loss of interest, fatigue, sleep changes and feeling worthless or hopeless.",
        "**Stress and burnout.** When you've been holding it together for a long time, tears can be the release valve once you finally stop.",
        "**Anxiety.** Constant worry and overwhelm can spill over into crying, especially when you're tired.",
        "**Grief.** Loss can surface in waves, months or years later, triggered by small things.",
        "**Hormone changes.** Premenstrual dysphoric disorder (PMDD), pregnancy, the postpartum period, perimenopause and thyroid problems can all cause intense tearfulness.",
        "**Sleep deprivation.** Too little sleep makes emotions much harder to regulate.",
        "**Medication effects.** Starting, stopping or changing some medications can affect mood.",
        "**Pseudobulbar affect (PBA).** A neurological condition that causes sudden crying or laughing that doesn't match your mood. It can follow stroke, brain injury, MS, ALS, Parkinson's disease or dementia, and needs a neurologist's evaluation.",
      ],
    },
    {
      heading: "When crying might be depression",
      paragraphs: [
        "Crying spells are more likely to be depression when, for two weeks or more, you also:",
      ],
      list: [
        "Feel down, empty or numb most of the day",
        "Have lost interest in things you used to enjoy",
        "Sleep too much or too little, or feel exhausted",
        "Find it hard to concentrate or make decisions",
        "Feel worthless, guilty or like a burden",
      ],
      paragraphsAfter: [
        "A short screening questionnaire such as the PHQ-9 can help you and a clinician see the full picture. If depression came on quickly, see [[sudden depression|/sudden-depression]].",
      ],
    },
    {
      heading: "5 things that can help right now",
      paragraphs: [],
      list: [
        "**Let it happen, safely.** Fighting tears tends to prolong them. Step somewhere private if you can and let the wave pass.",
        "**Breathe slow and low.** A few breaths with a longer exhale than inhale help your body settle after crying.",
        "**Write down what came before.** Note the time, what you were doing and how you slept. Patterns often appear within a week or two.",
        "**Tell one person.** Saying “I've been crying a lot and I don't know why” to someone you trust lightens the load.",
        "**Cover the basics.** Sleep, food, water and a short walk outside won't fix depression, but skipping them makes every emotion harder to handle.",
      ],
    },
    {
      heading: "Treating the root cause",
      paragraphs: [
        "The right treatment depends on the cause, which is why an evaluation matters. Hormone-related tearfulness may need your OB-GYN or primary care doctor. Pseudobulbar affect needs a neurologist. When depression is the cause, therapy and medication help many people, and there are further options when they haven't worked.",
        "For adults with major depression that hasn't improved on antidepressants, [[transcranial magnetic stimulation (TMS)|/services/tms]] is a non-drug treatment that stimulates the brain circuits involved in mood. [[SAINT®|/saint]], an accelerated, FDA-cleared form of TMS, is delivered over five days. Neuro Wellness TMS Centers of America in Coral Springs, Florida is a Verified SAINT® Provider and also offers [[psychotherapy|/services/psychotherapy]] and [[medication management|/services/medication-management]].",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "You don't have to figure out the “why” alone. If crying spells have lasted two weeks or more, a conversation with a clinician can help you understand what's going on.",
        "Our team offers a free consultation. Call 954-827-9793.",
      ],
      links: [
        { to: "/contactus", text: "Schedule a free consultation" },
        {
          to: "/am-i-a-candidate-screener",
          text: "Take the depression screening",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Why do I keep crying for no reason?",
      answer:
        "Frequent unexplained crying is often linked to depression, stress or burnout, anxiety, grief, lack of sleep or hormone changes. Less commonly, it's caused by a neurological condition called pseudobulbar affect.",
    },
    {
      question: "Is crying a lot a sign of depression?",
      answer:
        "It can be. Tearfulness is a common symptom of depression, especially when it comes with low mood, loss of interest, fatigue, sleep changes or hopelessness lasting two weeks or more.",
    },
    {
      question: "Can hormones make you cry for no reason?",
      answer:
        "Yes. Premenstrual dysphoric disorder, pregnancy, the postpartum period, perimenopause and thyroid problems can all cause intense, hard-to-explain crying spells.",
    },
    {
      question: "What is pseudobulbar affect?",
      answer:
        "Pseudobulbar affect (PBA) is a neurological condition that causes sudden, uncontrolled crying or laughing that doesn't match how you feel. It can follow a stroke, brain injury, MS, ALS, Parkinson's disease or dementia.",
    },
    {
      question: "When should I see someone about crying spells?",
      answer:
        "If crying spells last two weeks or more, interfere with work or relationships, or come with hopelessness, reach out to a clinician. If you're thinking about harming yourself, call or text 988 now.",
    },
  ],
  sources: [
    {
      citation: "National Institute of Mental Health. Depression.",
      url: "https://www.nimh.nih.gov/health/publications/depression",
    },
    {
      citation: "National Institute of Mental Health. Perinatal Depression.",
      url: "https://www.nimh.nih.gov/health/publications/perinatal-depression",
    },
    {
      citation:
        "Cleveland Clinic. Pseudobulbar Affect (PBA): Causes, Symptoms & Treatment.",
      url: "https://my.clevelandclinic.org/health/diseases/17928-pseudobulbar-affect-pba",
    },
    {
      citation: "988 Suicide and Crisis Lifeline.",
      url: "https://988lifeline.org",
    },
  ],
  relatedLinks: [
    {
      title: "Depression treatment",
      href: "/services/depression",
      description: "How we evaluate and treat depression in Coral Springs.",
    },
    {
      title: "Why am I so tired all the time?",
      href: "/learn/always-tired-depression-fatigue",
      description: "How depression drains your energy, and what helps.",
    },
    {
      title: "Can depression cause physical pain?",
      href: "/learn/can-depression-cause-physical-pain",
      description: "Why depression hurts and what helps.",
    },
    {
      title: "High-Functioning Depression Symptoms: 10 Quiet Signs",
      href: "/high-functioning-depression-symptoms-treatment",
      description: "Everyday habits that can be quiet signs of depression.",
    },
    {
      title: "Depression in women",
      href: "/depression-in-women",
      description: "Hormones, life stages and what helps.",
    },
    {
      title: "Crisis resources",
      href: "/crisis-resources",
      description: "Who to call, any time of day.",
    },
  ],
});
